<template>
  <div
    class="add-task-due-date-field"
    :class="{ 'add-task-due-date-field--open': isOpen }"
    v-bind="$attrs"
  >
    <v-text-field
      ref="input"
      data-testid="datepicker-textfield"
      type="text"
      dense
      outlined
      readonly
      :label="label"
      :clearable="clearable"
      :error-messages="error"
      class="noTransform add-task-due-date-field__input"
      :value="displayValue"
      :hide-details="hideDetails ? true : 'auto'"
      :disabled="disabled"
      :placeholder="placeholder"
      @click.native.stop="openPanel"
      @click:clear.stop="onClear"
    >
      <template v-slot:append>
        <button
          type="button"
          class="add-task-due-date-field__calendar-btn"
          aria-label="Open date picker"
          data-testid="add-task-due-date-field__calendar-icon"
          @click.stop="openPanel"
        >
          <img
            src="/img/icons/fbc-icons/CalendarIcon.svg"
            alt=""
            aria-hidden="true"
            class="add-task-due-date-field__calendar-icon"
          />
        </button>
      </template>
    </v-text-field>

    <div
      v-show="isOpen"
      ref="panel"
      class="add-task-due-date-field__panel"
      :class="`add-task-due-date-field__panel--${panelPlacement}`"
      :style="panelStyle"
      data-testid="datepicker-menu"
      @mousedown.stop
      @click.stop
    >
      <div class="add-task-due-date-field__header">
        <div class="add-task-due-date-field__header-title">
          {{ displayMonth }}
        </div>
        <div class="add-task-due-date-field__header-nav">
          <button
            type="button"
            class="add-task-due-date-field__nav-btn"
            aria-label="Previous month"
            data-testid="add-task-due-date-field__prev-month"
            @click.stop="previousMonth"
          >
            <v-icon class="add-task-due-date-field__nav-icon">mdi-chevron-left</v-icon>
          </button>
          <button
            type="button"
            class="add-task-due-date-field__nav-btn"
            aria-label="Next month"
            data-testid="add-task-due-date-field__next-month"
            @click.stop="nextMonth"
          >
            <v-icon class="add-task-due-date-field__nav-icon">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>

      <v-date-picker
        v-model="draftDate"
        :picker-date.sync="pickerDate"
        no-title
        scrollable
        show-adjacent-months
        :first-day-of-week="0"
        color="primary"
        class="add-task-due-date-field__calendar"
        :min="resolvedMin"
        :max="resolvedMax"
        data-testid="datepicker-calendar"
        @mouseover.native="onCalendarDayHint"
        @mouseleave.native="hideDayTooltip"
        @focusin.native="onCalendarDayHint"
        @focusout.native="onCalendarFocusOut"
      />

      <div
        v-show="dayTooltip.visible"
        class="add-task-due-date-field__day-tooltip"
        :style="dayTooltip.style"
        role="tooltip"
        data-testid="add-task-due-date-field__blocked-due-tooltip"
      >
        {{ invalidDateTooltip }}
      </div>

      <div class="add-task-due-date-field__actions">
        <v-btn
          class="add-task-due-date-field__cancel-btn secondaryBtn"
          data-testid="datepicker-close-btn"
          @click.stop="onCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          class="add-task-due-date-field__save-btn primaryBtn"
          data-testid="datepicker-ok-btn"
          @click.stop="onSave"
        >
          Save
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import './AddTaskDueDateField.scss';

function toPickerMonth(isoDate) {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  return isoDate.slice(0, 7);
}

const PANEL_LAYOUT_DEFAULTS = {
  width: 320,
  gap: 8,
  estimatedHeight: 380,
};

function shiftPickerMonth(pickerDate, delta) {
  const [year, month] = pickerDate.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export default {
  name: 'AddTaskDueDateField',

  inheritAttrs: false,

  props: {
    value: {
      type: String,
      default: '',
    },
    min: {
      type: String,
      default: '',
    },
    max: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Select Due Date',
    },
    error: {
      type: [String, Array],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    blockedDueBefore: {
      type: String,
      default: '',
    },
    invalidDateTooltip: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isOpen: false,
      draftDate: '',
      pickerDate: toPickerMonth(''),
      panelStyle: {},
      panelPlacement: 'bottom',
      panelHome: null,
      ignoreNextClose: false,
      dayTooltip: {
        visible: false,
        style: {},
      },
    };
  },

  computed: {
    displayValue() {
      const iso = this.toIsoDate(this.value);
      if (!iso) return '';
      return this.$nova.formatDate(iso);
    },
    displayMonth() {
      if (!this.pickerDate) return '';
      const [year, month] = this.pickerDate.split('-').map(Number);
      const date = new Date(year, month - 1, 1);
      return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },
    resolvedMin() {
      return this.min || undefined;
    },
    resolvedMax() {
      return this.max || undefined;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        const iso = this.toIsoDate(val);
        this.draftDate = iso;
        if (iso) {
          this.pickerDate = toPickerMonth(iso);
        }
      },
    },
  },

  mounted() {
    window.addEventListener('resize', this.updatePanelPosition);
    window.addEventListener('scroll', this.updatePanelPosition, true);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updatePanelPosition);
    window.removeEventListener('scroll', this.updatePanelPosition, true);
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
    this.detachPanelFromBody();
  },

  methods: {
    attachPanelToBody() {
      const panel = this.$refs.panel;
      if (!panel || panel.parentElement === document.body) return;
      this.panelHome = panel.parentElement;
      document.body.appendChild(panel);
    },
    detachPanelFromBody() {
      const panel = this.$refs.panel;
      if (!panel || !this.panelHome) return;
      if (panel.parentElement === document.body) {
        this.panelHome.appendChild(panel);
      }
      this.panelHome = null;
    },
    closePanel() {
      this.hideDayTooltip();
      this.isOpen = false;
      document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
      this.$nextTick(() => {
        this.detachPanelFromBody();
      });
    },
    toIsoDate(val) {
      if (!val) return '';
      const iso = this.$nova.formatDateISO(val);
      return iso || '';
    },
    openPanel() {
      if (this.disabled) return;
      const iso = this.toIsoDate(this.value);
      this.draftDate = iso;
      this.pickerDate = toPickerMonth(iso);
      this.ignoreNextClose = true;
      this.isOpen = true;
      this.$nextTick(() => {
        this.attachPanelToBody();
        this.updatePanelPosition();
        document.addEventListener('mousedown', this.onDocumentMouseDown, true);
        requestAnimationFrame(() => {
          this.updatePanelPosition();
          this.ignoreNextClose = false;
        });
      });
    },
    updatePanelPosition() {
      if (!this.isOpen || !this.$refs.input?.$el) return;

      const { width: panelWidth, gap: panelGap, estimatedHeight: panelEstimatedHeight } = this.getPanelLayout();
      const inputEl = this.$refs.input.$el;
      const panelEl = this.$refs.panel;
      const rect = inputEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const panelHeight = panelEl?.offsetHeight || panelEstimatedHeight;

      const spaceBelow = viewportHeight - rect.bottom - panelGap;
      const spaceAbove = rect.top - panelGap;
      const openAbove = spaceBelow < panelHeight && spaceAbove >= spaceBelow;

      this.panelPlacement = openAbove ? 'top' : 'bottom';

      let top = openAbove
        ? rect.top - panelHeight - panelGap
        : rect.bottom + panelGap;
      top = Math.max(panelGap, Math.min(top, viewportHeight - panelHeight - panelGap));

      let left = rect.left;
      if (left + panelWidth > viewportWidth - panelGap) {
        left = Math.max(panelGap, viewportWidth - panelWidth - panelGap);
      }

      this.panelStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${panelWidth}px`,
        zIndex: 'var(--z-dialog-menu)',
      };
    },
    getPanelLayout() {
      const root = this.$el;
      if (!root) return { ...PANEL_LAYOUT_DEFAULTS };

      const styles = getComputedStyle(root);
      const readPx = (token, fallback) => {
        const parsed = parseFloat(styles.getPropertyValue(token));
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      return {
        width: readPx('--add-task-due-date-panel-width', PANEL_LAYOUT_DEFAULTS.width),
        gap: readPx('--add-task-due-date-panel-gap', PANEL_LAYOUT_DEFAULTS.gap),
        estimatedHeight: readPx('--add-task-due-date-panel-estimated-height', PANEL_LAYOUT_DEFAULTS.estimatedHeight),
      };
    },
    onDocumentMouseDown(event) {
      if (!this.isOpen || this.ignoreNextClose) return;

      const target = event.target;
      const inputEl = this.$refs.input?.$el;
      const panelEl = this.$refs.panel;

      if (inputEl?.contains(target) || panelEl?.contains(target)) {
        return;
      }

      this.onCancel();
    },
    onCancel() {
      this.draftDate = this.toIsoDate(this.value);
      this.closePanel();
    },
    onSave() {
      this.$emit('input', this.draftDate || '');
      this.$emit('change', this.draftDate || '');
      this.closePanel();
    },
    onClear() {
      this.draftDate = '';
      this.$emit('input', '');
      this.$emit('change', '');
      this.closePanel();
    },
    hideDayTooltip() {
      if (!this.dayTooltip.visible) return;
      this.dayTooltip.visible = false;
    },
    dateFromDayButton(button) {
      const table = button.closest('.v-date-picker-table');
      if (!table || !this.pickerDate) return '';
      const cells = [...table.querySelectorAll('tbody td')];
      const cell = button.closest('td');
      const index = cells.indexOf(cell);
      if (index < 0) return '';
      const [year, month] = this.pickerDate.split('-').map(Number);
      if (!year || !month) return '';
      const first = new Date(year, month - 1, 1);
      const dayNumber = index - first.getDay() + 1;
      const date = new Date(year, month - 1, dayNumber);
      const isoMonth = String(date.getMonth() + 1).padStart(2, '0');
      const isoDay = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${isoMonth}-${isoDay}`;
    },
    onCalendarDayHint(event) {
      const button = event.target?.closest?.('.v-date-picker-table button');
      if (!button || !this.blockedDueBefore || !this.invalidDateTooltip) {
        this.hideDayTooltip();
        return;
      }
      if (button.disabled || button.classList.contains('v-btn--disabled')) {
        this.hideDayTooltip();
        return;
      }
      const iso = this.dateFromDayButton(button);
      if (!iso || iso < this.blockedDueBefore) {
        this.hideDayTooltip();
        return;
      }
      const rect = button.getBoundingClientRect();
      this.dayTooltip.style = {
        top: `${rect.bottom + 10}px`,
        left: `${rect.left + rect.width / 2}px`,
        transform: 'translateX(-50%)',
      };
      this.dayTooltip.visible = true;
    },
    onCalendarFocusOut(event) {
      const next = event.relatedTarget;
      if (next?.closest?.('.v-date-picker-table button')) return;
      this.hideDayTooltip();
    },
    previousMonth() {
      this.pickerDate = shiftPickerMonth(this.pickerDate, -1);
    },
    nextMonth() {
      this.pickerDate = shiftPickerMonth(this.pickerDate, 1);
    },
  },
};
</script>
